// What a workspace's Documents list shows, kept while the app runs and never in
// the URL (how a list looks is not something to hand to someone): the space it
// is narrowed to, documents in use or archived ones, and the space picked for
// the next new document. One store per app (made by `workspaceDocumentsScreen`),
// one state per workspace. Also the space last used for a new document, kept
// on this device.
import type { SpaceView } from "@trameria/cloud-api/workspace-shared";
import {
  type DocumentsShown,
  defineDeviceSetting,
  useDeviceSetting,
} from "@trameria/ui-core";
import {
  createContext,
  useCallback,
  useContext,
  useSyncExternalStore,
} from "react";

export interface DocsState {
  spaceId: string | null;
  shown: DocumentsShown;
  /** Picked on the new document's screen (before it is made). */
  newSpaceId: string | null;
}

const INITIAL: DocsState = { spaceId: null, shown: "active", newSpaceId: null };

export interface DocsStore {
  get(slug: string): DocsState;
  set(slug: string, patch: Partial<DocsState>): void;
  subscribe(listener: () => void): () => void;
}

export function createDocsStore(): DocsStore {
  const states = new Map<string, DocsState>();
  const listeners = new Set<() => void>();
  return {
    get: (slug) => states.get(slug) ?? INITIAL,
    set(slug, patch) {
      states.set(slug, { ...(states.get(slug) ?? INITIAL), ...patch });
      for (const l of listeners) l();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

export const DocsStoreContext = createContext<DocsStore | null>(null);

export function useDocsState(
  slug: string,
): [DocsState, (patch: Partial<DocsState>) => void] {
  const store = useContext(DocsStoreContext);
  if (!store) throw new Error("useDocsState outside the documents screens");
  const state = useSyncExternalStore(
    store.subscribe,
    () => store.get(slug),
    () => store.get(slug),
  );
  const set = useCallback(
    (patch: Partial<DocsState>) => store.set(slug, patch),
    [store, slug],
  );
  return [state, set];
}

const lastSpaceSetting = defineDeviceSetting<Readonly<Record<string, string>>>({
  key: "trameria-last-document-space",
  parse: (raw) => {
    try {
      const v: unknown = raw ? JSON.parse(raw) : {};
      return v && typeof v === "object" ? (v as Record<string, string>) : {};
    } catch {
      return {};
    }
  },
  serialize: (v) => JSON.stringify(v),
});

/** The space a new document was last made in, in this workspace. */
export function useLastDocumentSpace(
  slug: string,
): [string | null, (spaceId: string) => void] {
  const [all, setAll] = useDeviceSetting(lastSpaceSetting);
  const set = useCallback(
    (spaceId: string) =>
      setAll({ ...lastSpaceSetting.read(), [slug]: spaceId }),
    [slug, setAll],
  );
  return [all[slug] ?? null, set];
}

/**
 * The space a new document goes to: the one picked for it, else the one the
 * list is narrowed to, else the one last used here, else the first.
 */
export function newDocumentSpace(
  spaces: readonly SpaceView[],
  state: DocsState,
  last: string | null,
): SpaceView | null {
  return (
    spaces.find((s) => s.id === state.newSpaceId) ??
    spaces.find((s) => s.id === state.spaceId) ??
    spaces.find((s) => s.id === last) ??
    spaces[0] ??
    null
  );
}
